import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DayPicker } from "react-day-picker";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";
import { Popup } from "@progress/kendo-react-popup";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { setWorkflowCustomVariables } from "../../actions/taskSidebar";
import { DESC_TYPE } from "../../components/TaskSidebar/sidebar.constants";
import { useWorkflowContext } from "../../components/Workflow/WorkflowContextProvider";
import { addRecentUser } from "../../components/Tasks/tasks.service";
import { icon, label, number, tooltip, quote } from "../../config";
import { errorCode } from "../../config/constants";
import { useEditorContext } from "../editor.Context";
import {
  arrayOfObjects,
  formatDate,
  generateTimeArray,
  generateUniqueId,
  getBottomIconsConfig,
  getTabLabels,
  searchUsers,
} from "../editor.helper";
import { getUserRecentHistory } from "../../components/RecentHistory/recentHistory.service";
import { getSearchResult } from "../../components/OverallSearch/overallsearch.service";
import { getProjectVariables } from "../../shared/services/projectVariables.service";
import { getWorkflowDescriptionVariables } from "./TaskCustomVariables/services/workflowDescriptionVariable.service";
import { appendProjectVariablesToWorkflowList } from "../../shared/utils/projectVariablesChipMerge.utils";
import store from "../../store/index";
import { editorEntity } from "../editor.Constant";
import _ from "lodash";
import BookmarkTool from "../editorTools/BookmarkTool";
import CustomVariableDates from "./TaskCustomVariables/CustomVariableDates";
import CustomVariableDropdown from "./TaskCustomVariables/CustomVariableDropdown";
import CustomVariableInput from "./TaskCustomVariables/CustomVariableInput";
import CustomVariableNumbers from "./TaskCustomVariables/CustomVariableNumbers";
import ChipsTab from "./components/ChipsTab";
import UsersTab from "./components/UsersTab";
import TaskIdTab from "./components/TaskIdTab";
import EmailVariables from "./components/EmailVariables";
/**
 * Checks if two dates are the same calendar day (ignoring time-of-day).
 * @param {Date} a - First date
 * @param {Date} b - Second date
 * @returns {boolean} True if dates are the same calendar day, false otherwise
 * @author Sejal 
 */
const isSameCalendarDay = (date, selectedDate) => {
  if (!date || !selectedDate) return false;
  return (
    date.getFullYear() === selectedDate.getFullYear() &&
    date.getMonth() === selectedDate.getMonth() &&
    date.getDate() === selectedDate.getDate()
  );
};

/**
 * Date chip component for inserting date chips in the editor
 * @param {Object} props - Component props
 * @param {Function} props.updateNonEditable - Function to update non-editable chip
 * @author Shivam Mishra
 */
const DateChip = ({ updateNonEditable }) => {
  const { user } = useSelector((state) => state.auth);
  const [selected, setSelected] = useState(new Date());
  /**
   * Function to handle insert date chip..
   * @author Shivam Mishra
   */  const handleInsertNode = useCallback(
    async (date) => {
      const id = await generateUniqueId(user.id, "date", null);
      updateNonEditable(formatDate(date || selected), id, "date-chip");
    },
    [user.id, selected, updateNonEditable]
  );

  return (
    <div className='zoom'>
      <h6 className="p-2 ml-2">{label.SELECT_DATE}</h6>
      <DayPicker
        mode='single'
        selected={selected}
        onSelect={(date) => {
          if (date) {
            setSelected(date);
            handleInsertNode(date);
          }
        }}
        onDayClick={(day, activeModifiers) => {
          if (activeModifiers.disabled || activeModifiers.hidden) return;
          if (isSameCalendarDay(day, selected)) {
            handleInsertNode(day);
          }
        }}
        id='editor-smart-date-picker'
      />
    </div>
  );
};

/**
 * Inserts dd Chip  for Editor
 * PC SmartChip
 * @author Shivam Mishra
 */
const DropDownChip = ({ updateNonEditable }) => {
  const { user } = useSelector((state) => state.auth);
  const firstElements = arrayOfObjects?.map((obj) => ({ name: obj.value[0], id: obj.id }));
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [showDetailPopup, setShowDetailPopup] = useState(false);
  const ddRef = useRef(null);

  const anchorAlign = {
    horizontal: "right",
    vertical: "center",
  };

  const popupAlign = {
    horizontal: "left",
    vertical: "top",
  };

  useEffect(() => {
    return () => {
      ddRef.current = null;
    };
  }, []);

  /**
   * Function to update ref and open respective dd.
   * @param {Integer}id  of dd .
   * @author Shivam Mishra
   */
  const handleMouseEnter = useCallback(async (id) => {
    setShowDetailPopup(true);
    setHoveredIndex(id);
    ddRef.current = document.getElementById(`dd-options-${id}`);
  }, []);

  /**
   * Function to handle insert dd chip.
   * @param {String}Name Name of dd .
   * @author Shivam Mishra
   */
  const updateNode = useCallback(
    async (event, name) => {
      event.preventDefault();
      const id = await generateUniqueId(user.id, "dd", hoveredIndex);
      await updateNonEditable(name, id);
    },
    [hoveredIndex, user.id, updateNonEditable]
  );

  return (
    <div className='dt-editor-users overflow-auto w-100'>
      {firstElements?.map(({ name, id }, index) => (
        <div
          className='dt-editor-users-item cursor-pointer'
          key={index}
          onMouseEnter={() => handleMouseEnter(id)}
          id={`dd-options-${id}`}
          ref={hoveredIndex === index ? ddRef : null}
          onClick={(event) => updateNode(event, name)}>
          {name}
        </div>
      ))}
      <Popup show={showDetailPopup} anchor={ddRef.current} anchorAlign={anchorAlign} popupAlign={popupAlign}>
        {arrayOfObjects
          .find((obj) => obj.id === hoveredIndex)
          ?.value?.map((name, index) => (
            <div key={index} className='dt-editor-users cursor-pointer word-break-all overflow-auto w-100'>
              <div
                className='dt-editor-users-item cursor-pointer'
                id={`editor-smart-dd${index}-picker`}
                onClick={(event) => updateNode(event, name)}>
                {name}
              </div>
            </div>
          ))}
      </Popup>
    </div>
  );
};


const TimeChip = ({ updateNonEditable }) => {
  const { user } = useSelector((state) => state.auth);
  const [selectedTime, setSelectedTime] = useState("00:00");
  const timeArray = useMemo(() => generateTimeArray(), []);

  /**
   * Function to handle insert date chip.
   * @param {Null} .
   * @author Shivam Mishra
   */
  const handleInsertNode = useCallback(
    async (time) => {
      const id = await generateUniqueId(user.id, "time", null);
      updateNonEditable(time || selectedTime, id, "time-chip");
    },
    [user.id, selectedTime, updateNonEditable]
  );

  return (
    <div className='p-3 overflow-hidden'>
      <h6>{label.SELECT_TIME}</h6>
      <div>
        <div className='mb-3 position-relative' id='editor-smart-time-picker'>
          <div className='time-options w-100 overflow-y-auto'>
            {timeArray.map((time) => (
              <div
                key={time}
                className={`time-option cursor-pointer ${time === selectedTime ? "blue active-time-list" : ""}`}
                onClick={() => {
                  setSelectedTime(time);
                  handleInsertNode(time);
                }}>
                {time}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const componentsData = {
  DateChip: { component: DateChip, text: "Date", icon: icon.PLAN_DO_TOMORROW },
  TimeChip: { component: TimeChip, text: "Time", icon: icon.ALARM },
  BookmarkTool: { component: BookmarkTool, text: "Bookmark", icon: icon.BOOKMARK_TOOL },
};

/**
 * Inserts Chip  for Editor
 * PC KendoEditor
 * @author Shivam Mishra
 */
const SmartChip = ({
  updateNonEditable,
  assigneeList,
  setAssigneeList,
  acknowledgeMailEditor,
  editorRef,
  hidePopup,
  taskKey,
  insertTaskChip,
  workflowDescriptionProjectId,
}) => {
  const { defaultDetails } = useSelector((state) => state.tasks);
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [selectedCustomVariable, setSelectedCustomVariable] = useState(null);
  const { user } = useSelector((state) => state.auth);
  const { originalWorkflowVariables } = useSelector((state) => state.taskSidebar);
  const editorState = useEditorContext();
  const {
    selectedIndex,
    setSelectedIndex,
    containerRef,
    tempChipID,
    setChipTarget,
    taskCustomVariablesRef,
    activeTab,
    setActiveTab,
  } = editorState;
  const [chipsSearchText, setChipsSearchText] = useState("");
  const [taskSearchText, setTaskSearchText] = useState("");
  const [taskSearchResults, setTaskSearchResults] = useState([]);
  const [showTaskSearchLoader, setShowTaskSearchLoader] = useState(false);
  const taskSearchControllerRef = useRef(null);
  const { recentHistoryList } = useSelector((state) => state.history);
  const { recentAssignmentList } = useSelector((state) => state.auth);
  const isDrutasDox = taskKey === label.DOX_TASK_KEY;
  const dispatch = useDispatch();
  const { projectId: routeProjectId } = useParams();
  const projectIdForWorkflowDesignerChips = workflowDescriptionProjectId ?? routeProjectId;
  const workflowContext = useWorkflowContext();
  const workflowIdForDesignerChips = workflowContext?.workflow?.Id;
  
  const componentProps = {
    updateNonEditable: updateNonEditable,
    editorRef: editorRef,
    taskKey: taskKey,
  };

  /**
   * useeffect to set default selected option.
   * @author Shivam Mishra
   */
    useEffect(() => {
    setSelectedIndex(number.ZERO);
  }, [assigneeList, setSelectedIndex]);

  /**
   * Global keyboard event listener for custom variable components
   */
  useEffect(() => {
    if (activeTab === "taskId" && !recentHistoryList.length) {
      dispatch(getUserRecentHistory(user.id, number.THREE, number.ZERO, false));
    }
  }, [activeTab, recentHistoryList.length, user.id]);

  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      // Only handle events when custom variable components are rendered
      if (selectedCustomVariable) {
        if (e.key === "Escape") {
          e.preventDefault();
          e.stopPropagation();
          setSelectedCustomVariable(null);
          setSelectedComponent(null);
          hidePopup();
        }
      }
    };

    // Add global event listener
    document.addEventListener("keydown", handleGlobalKeyDown, true);

    // Cleanup
    return () => {
      document.removeEventListener("keydown", handleGlobalKeyDown, true);
    };
  }, [selectedCustomVariable, hidePopup]);

  /**
   * Function to handle option select.
   * @param {String} componentName Name of the selected component.
   * @author Shivam Mishra
   */
  const handleOptionSelect = useCallback(
    (componentName) => {
      if (componentName === "BookmarkTool") {
        updateNonEditable(null, null, "Bookmark");
      } else {
        setSelectedComponent(componentName);
        setSelectedCustomVariable(null);
      }
    },
    [updateNonEditable]
  );

  /**
   * Function to update node.
   * @param {Object} assignee Object containing label and value.
   * @author Shivam Mishra
   */
  const updateNode = useCallback(
    async (assignee) => {
      const { label, value } = assignee;
      const id = await generateUniqueId(user.id, "user", value);
      updateNonEditable(label, id, "user-chip");
      await dispatch(addRecentUser({ userId: user.id, recentUserId: assignee.value }));
    },
    [user.id, updateNonEditable]
  );

  /**
   * Function to update assignee list.
   * @param {e} event.
   * @author Shivam Mishra
   */
  const updateUserList = useCallback(
    (e) => {
      setAssigneeList(searchUsers(e.target.value, defaultDetails, assigneeList));
    },
    [defaultDetails, assigneeList, setAssigneeList]
  );

  /**
   * Finds the next valid index skipping subheadings
   * @param {number} currentIndex - Current index in the list
   * @param {number} direction - Direction to move (1 for down, -1 for up)
   * @returns {number} Next valid index skipping subheadings
   * @author Abhishek Dhillon
   */
  const findNextValidIndex = useCallback((currentIndex, direction) => {
    const maxIndex = assigneeList.length - number.ONE;
    let newIndex = direction > number.ZERO 
      ? Math.min(maxIndex, currentIndex + number.ONE)
      : Math.max(number.ZERO, currentIndex - number.ONE);
    
    let iterations = 0;
    const maxIterations = assigneeList.length;
    
    // Skip subheadings with safety check to prevent infinite loops
    while (assigneeList[newIndex]?.isSubHeading && iterations < maxIterations) {
      const nextIndex = direction > number.ZERO 
        ? Math.min(maxIndex, newIndex + number.ONE)
        : Math.max(number.ZERO, newIndex - number.ONE);
      
      // If we can't move further (hit boundary), break to prevent infinite loop
      if (nextIndex === newIndex) {
        break;
      }
      
      newIndex = nextIndex;
      iterations++;
    }
    
    return newIndex;
  }, [assigneeList]);

  /**
   * Handles Enter key press to select the current assignee
   * @author Abhishek Dhillon
   */
  const handleEnterKey = useCallback(() => {
    if (selectedCustomVariable) {
      return; // Let the custom variable component handle Enter key
    }
    if (assigneeList.length > number.ZERO && assigneeList[selectedIndex]) {
      updateNode(assigneeList[selectedIndex]);
    }
  }, [selectedCustomVariable, assigneeList, selectedIndex, updateNode]);

  /**
   * Handles Arrow Up key press to navigate to previous assignee
   * @author Abhishek Dhillon
   */
  const handleArrowUp = useCallback(() => {
    setSelectedIndex((prevIndex) => findNextValidIndex(prevIndex, -1));
  }, [setSelectedIndex, findNextValidIndex]);

  /**
   * Handles Arrow Down key press to navigate to next assignee
   * @author Abhishek Dhillon
   */
  const handleArrowDown = useCallback(() => {
    setSelectedIndex((prevIndex) => findNextValidIndex(prevIndex, 1));
  }, [setSelectedIndex, findNextValidIndex]);

  /**
   * Function to handle keyboard navigation for assignee list
   * @param {KeyboardEvent} e - Keyboard event
   * @author Abhishek Dhillon
   */
  const handleKeyDown = useCallback((e) => {
    const keyHandlers = {
      Enter: () => {
        e.preventDefault();
        handleEnterKey();
      },
      ArrowUp: handleArrowUp,
      ArrowDown: handleArrowDown,
      Escape: hidePopup,
    };

    const handler = keyHandlers[e.key];
    if (handler) {
      handler();
    }
  }, [handleEnterKey, handleArrowUp, handleArrowDown, hidePopup]);

  const getCustomVariableComponent = useCallback((variableDetails) => {
    switch (variableDetails?.type) {
      case "dropdown":
        return CustomVariableDropdown;
      case "text":
        return CustomVariableInput;
      case "date":
        return CustomVariableDates;
      case "number":
        return CustomVariableNumbers;
      default:
        return null;
    }
  }, []);

  const smartChipClickHandler = useCallback(
    (variableDetails) => {
      setSelectedCustomVariable({
        component: getCustomVariableComponent(variableDetails),
        props: { ...componentProps, variableDetails, hidePopup },
      });
      setSelectedComponent(null);
      const chipElement = document.getElementById(tempChipID);
      setChipTarget(null);
      setTimeout(() => setChipTarget(chipElement), number.HUNDRED);
    },
    [getCustomVariableComponent, componentProps, hidePopup, tempChipID, setChipTarget]
  );

  const updateEmailVariable = useCallback(
    (variable) => {
      const { name, varId } = variable;
      const id = generateUniqueId(user.id, varId, varId);
      updateNonEditable(name, id, "user-chip");
    },
    [user.id, updateNonEditable]
  );

  /**
   * Renders the list of assignees or a no-result message
   * @returns {JSX.Element}
   * @author Abhishek Dhillon
   */
  const renderAssigneeList = useCallback(() => {
    if (assigneeList?.length === 0) {
      return <div className='no-result'>{quote?.NO_RESULT_FOUND}</div>;
    }
    return assigneeList?.map((assignee, index) => {
      if (assignee?.isSubHeading) {
        return (
          <React.Fragment key={index}>
            {recentAssignmentList?.find((recentItem) => recentItem?.AssignedUserId) ? (
              <span className='dropdown-subheading mt-2'>{assignee?.label}</span>
            ) : null}
          </React.Fragment>
        );
      }
      return (
        <div
          className={`dt-editor-users-list cursor-pointer d-flex align-items-center ${index === selectedIndex ? "user-selected" : ""}`}
          key={index}
          onClick={() => updateNode(assignee)}>
          <div className='mr-2'>
            {assignee?.PhotoLinks?.small ? (
              <img src={assignee?.PhotoLinks?.small} className='userlist-dp rounded-circle' alt={assignee?.label} />
            ) : (
              <div className='userlist-icon'>{icon.ACCOUNT_CIRCLE}</div>
            )}
          </div>
          <div>
            <div className='assignee-info d-block'>{assignee?.label}</div>
            <div className='small info-text'>{assignee?.Email}</div>
          </div>
        </div>
      );
    });
  }, [assigneeList, selectedIndex, recentAssignmentList, updateNode]);


  /**
   * Gets the selected component based on the component type
   * @returns {JSX.Element|null} The selected component or null
   * @author Abhishek Dhillon
   */
  const componentMap = {
    DateChip: DateChip,
    TimeChip: TimeChip,
    BookmarkTool: BookmarkTool,
  };

  const getSelectedComponent = useCallback(() => {
    if (!selectedComponent) return null;
    const Component = componentMap[selectedComponent];
    return Component ? <Component {...componentProps} /> : null;
  }, [selectedComponent, componentProps]);

  /**
   * Gets the selected custom variable based on the component type
   * @returns {JSX.Element|null} The selected custom variable or null
   * @author Abhishek Dhillon
   */
  const getSelectedCustomVariable = useCallback(() => {
    if (!selectedCustomVariable) return null;
    const Component = selectedCustomVariable.component;
    return <Component {...selectedCustomVariable.props} />;
  }, [selectedCustomVariable]);

  const checkWorkflowCustomVariables = useCallback(() => {
    return originalWorkflowVariables ?? [];
  }, [originalWorkflowVariables]);

  const filteredChips = useMemo(() => {
    if (!chipsSearchText) return checkWorkflowCustomVariables();
    const searchLower = chipsSearchText.toLowerCase();
    return checkWorkflowCustomVariables()?.filter((variable) => variable?.name?.toLowerCase().includes(searchLower));
  }, [chipsSearchText, checkWorkflowCustomVariables]);

  /**
   * Extracts task results from API search response
   * @author Muskan Thakur
   */
  const extractTaskResults = useCallback((result) => {
    if (!result || !Array.isArray(result) || result.length === 0) {
      return [];
    }

    const strategies = [
      () => result.find((entity) => entity.Id === number.FOUR),
      () => result.find((entity) => entity.Name === label.TASK || entity.Name === label.TASKS),
      () => result[0]?.searchResult ? result[0] : null,
    ];

    for (const strategy of strategies) {
      const taskEntity = strategy();
      if (taskEntity?.searchResult && Array.isArray(taskEntity.searchResult)) {
        return taskEntity.searchResult;
      }
    }

    return [];
  }, []);

  /**
   * Debounced search for tasks using API
   * @author Muskan Thakur
   */
  const debouncedTaskSearch = useCallback(
    _.debounce(async (searchValue) => {
      if (searchValue && searchValue.trim().length > 1) {
        setShowTaskSearchLoader(true);
        if (taskSearchControllerRef.current) {
          taskSearchControllerRef.current.abort();
        }
        taskSearchControllerRef.current = new AbortController();
        
        const result = await dispatch(
          getSearchResult(
            {
              userId: user.id,
              companyId: user.companyId,
              searchText: searchValue,
              entityName: editorEntity.TASK_DESC,
            },
            taskSearchControllerRef.current?.signal
          )
        );

        if (result !== errorCode.CANCEL_REQUEST) {
          setShowTaskSearchLoader(false);
          const taskResults = extractTaskResults(result);
          setTaskSearchResults(taskResults);
        }
      } else {
        setShowTaskSearchLoader(false);
        setTaskSearchResults([]);
      }
    }, number.FIVE_HUNDRED),
    [user.id, user.companyId]
  );

  /**
   * Handle task search input change
   * @author Muskan Thakur
   */
  const handleTaskSearch = useCallback(
    (e) => {
      const value = e.target.value;
      setTaskSearchText(value);
      debouncedTaskSearch(value);
    },
    []
  );

  const bottomIconsConfig = getBottomIconsConfig(icon, tooltip);

  const renderBottomIcons = useCallback(() => {
    return (
      <div className='d-flex justify-content-around align-items-center p-2 border-top'>
        {bottomIconsConfig.map(({ componentName, icon: iconComponent, tooltip: tooltipText }) => (
          <Tooltip key={componentName} anchorElement='target' parentTitle={true} position='top'>
            <div
              title={tooltipText}
              className='cursor-pointer app-icon-medium'
              onClick={() => handleOptionSelect(componentName)}>
              {iconComponent}
            </div>
          </Tooltip>
        ))}
      </div>
    );
  }, [handleOptionSelect]);

  const tabLabels = getTabLabels(label);

  const getTitle = useCallback(() => {
    return tabLabels[activeTab] || label.USER;
  }, [activeTab, tabLabels]);

  /**
   * Gets the dynamic placeholder text based on the active tab
   * @returns {string} Placeholder text with the appropriate shortcut key
   * @author Muskan Thakur
   */
  const getPlaceholderText = useCallback(() => {
    switch (activeTab) {
      case "chips":
        return "type ~ to open";
      case "users":
        return "type @ to open";
      case "taskId":
        return "type # to open";
      default:
        return "type ~ to open";
    }
  }, [activeTab]);

  /**
   * Handles tab selection for smart chip tabs
   * @param {string} tab - Selected tab key
   * @param {boolean} disabled - Indicates whether tab selection is disabled
   * @memberof SmartChip
   * @author Abhishek Dhillon
   */
  const handleTabClick = useCallback((tab, disabled) => {
    setSelectedIndex(number.ZERO);
    if (!disabled) {
      setActiveTab(tab);
    }
  }, []);

  return (
    <div>
      {!selectedComponent && !selectedCustomVariable ? (
        <div className='dt-editor-menu'>
          <div className='d-flex justify-content-between align-items-center p-2 border-bottom'>
            <div className="line-height-normal">
              <div className='font-weight-bold line-height-normal'>{getTitle()}</div>
              <span className="small text-muted line-height-normal">{getPlaceholderText()}</span>
            </div>
            <div className='d-flex align-items-center'>
              <span className='small text-muted mr-2'>{label.ESC_PRESS}</span>
              <span className='square-icon square-icon-medium' onClick={hidePopup}>
                <Tooltip anchorElement='target' parentTitle={true} position='bottom'>
                  {icon.CLOSE_EDITOR}
                </Tooltip>
              </span>
            </div>
          </div>

          <div className='d-flex justify-content-around align-items-center p-2 border-bottom'>
            {[
              {
                tab: "chips",
                icon: icon.MICROCHIP,
                label: label.CHIPS,
                disabled: isDrutasDox,
                disabledLabel: label.DISABLED,
              },
              { tab: "users", icon: icon.ACCOUNT_CIRCLE, label: label.USER },
              { tab: "taskId", icon: icon.CHECK_CIRCLE, label: label.TASK_ID },
            ].map(({ tab, icon: tabIcon, label: tabLabel, disabled, disabledLabel }) => (
              <Tooltip key={tab} anchorElement='target' parentTitle={true} position='top'>
                <div
                  className={`cursor-pointer d-flex align-items-center justify-content-center smart-chip-tab-icon app-icon-medium ${activeTab === tab ? "active" : ""} ${disabled ? "disabled opacity-50" : ""}`}
                  onClick={() => handleTabClick(tab, disabled)}
                  title={disabled ? disabledLabel : tabLabel}>
                  {tabIcon}
                </div>
              </Tooltip>
            ))}
          </div>

          <div className='custom-editor-section'>
            {activeTab === "chips" && (
              <ChipsTab
                filteredChips={filteredChips}
                chipsSearchText={chipsSearchText}
                setChipsSearchText={setChipsSearchText}
                smartChipClickHandler={smartChipClickHandler}
                containerRef={containerRef}
                handleKeyDown={handleKeyDown}
              />
            )}
            {activeTab === "users" && (
              <UsersTab
                updateUserList={updateUserList}
                renderAssigneeList={renderAssigneeList}
                containerRef={containerRef}
                handleKeyDown={handleKeyDown}
              />
            )}
            {activeTab === "taskId" && (
              <TaskIdTab
                recentHistoryList={recentHistoryList}
                taskSearchText={taskSearchText}
                handleTaskSearch={handleTaskSearch}
                selectedIndex={selectedIndex}
                insertTaskChip={insertTaskChip}
                containerRef={containerRef}
                handleKeyDown={handleKeyDown}
                searchResults={taskSearchResults}
                showLoader={showTaskSearchLoader}
              />
            )}
          </div>

          {activeTab === "users" && (
            <EmailVariables
              acknowledgeMailEditor={acknowledgeMailEditor}
              taskKey={taskKey}
              updateEmailVariable={updateEmailVariable}
            />
          )}

          {renderBottomIcons()}
        </div>
      ) : (
        <>
          {getSelectedComponent()}
          {getSelectedCustomVariable()}
        </>
      )}
    </div>
  );
};

export default React.memo(SmartChip);
